import { useRef, useState } from 'react';
import { ApiError } from '@/api/client';
import {
  useUpdateEvent,
  useUploadPoster,
  useUploadRegulation,
} from '@/api/queries';
import type { EventCard } from '@/api/types';
import { Button } from '@/components/kit/Button';
import { ConditionItem, ConditionList, TextLink } from '@/components/kit/Controls';
import { DateField } from '@/components/kit/DatePicker';
import { Col, Form, Row, TextField } from '@/components/kit/Field';
import { Columns, Kv } from '@/components/kit/Layout';
import { MetricRow } from '@/components/kit/MetricRow';
import { EmptyState, Note } from '@/components/kit/Note';
import { Panel, Stack } from '@/components/kit/Panel';
import { useToast } from '@/components/kit/Toast';
import { num } from '@/lib/format';

/**
 * Вкладка «Основное» — обзор мероприятия (кит 2.11): паспорт цифр,
 * панели настроек и положения, сбоку афиша и готовность к публикации.
 *
 * Поля сохраняются по уходу фокуса, а не по кнопке: экран правится
 * по одному полю, и «Сохранить» здесь только добавляет шаг,
 * на котором можно забыть нажать.
 */
export function MainTab({ event }: { event: EventCard }) {
  const update = useUpdateEvent(event.id);
  const toast = useToast();

  const days =
    Math.round(
      (new Date(event.dateTo).getTime() - new Date(event.dateFrom).getTime()) / 86_400_000,
    ) + 1;

  const save = (patch: Record<string, unknown>) =>
    update.mutate(patch, {
      onSuccess: () => toast.show('Сохранено'),
      onError: (e) => toast.showError(e instanceof ApiError ? e.message : 'Не сохранилось'),
    });

  return (
    <>
      {/* Паспорт мероприятия — ряд цифр обзора (кит 2.11). Город, площадка
          и даты не повторяются фактами: они правятся в настройках ниже */}
      <MetricRow
        variant="sum"
        items={[
          { label: 'Дней', value: num(days) },
          { label: 'Номинаций', value: num(event.nominations.length), dim: !event.nominations.length },
          {
            label: 'Лимит команд',
            value: num(event.computed.teamLimitTotal),
            dim: !event.computed.teamLimitTotal,
          },
          {
            label: 'Проверок пройдено',
            value: `${event.readiness.filter((c) => c.ok).length} из ${event.readiness.length}`,
          },
        ]}
      />

      <Columns>
        <Stack>
          <Panel title="Настройки мероприятия">
            <Form>
              <TextField
                label="Город"
                defaultValue={event.city}
                key={`city-${event.city}`}
                onBlur={(e) => {
                  const next = e.target.value.trim();
                  if (next && next !== event.city) save({ city: next });
                }}
              />
              <TextField
                label="Площадка"
                defaultValue={event.venue ?? ''}
                key={`venue-${event.venue}`}
                onBlur={(e) => {
                  const next = e.target.value.trim();
                  if (next !== (event.venue ?? '')) save({ venue: next || null });
                }}
              />

              <Row>
                <Col>
                  <DateField
                    label="Дата начала"
                    value={event.dateFrom}
                    onChange={(v) => save({ dateFrom: v })}
                  />
                </Col>
                <Col>
                  <DateField
                    label="Дата окончания"
                    value={event.dateTo}
                    onChange={(v) => save({ dateTo: v })}
                  />
                </Col>
              </Row>

              <Row>
                <Col>
                  <DateField
                    label="Приём заявок до"
                    value={event.applyUntil}
                    onChange={(v) => save({ applyUntil: v })}
                  />
                </Col>
                <Col>
                  <DateField
                    label="Повышение цены по умолчанию"
                    value={event.priceRaiseDefault}
                    onChange={(v) => save({ priceRaiseDefault: v })}
                    alignRight
                  />
                </Col>
              </Row>
            </Form>

            <Note>
              Дата повышения здесь — <b>значение для новых пакетов</b>. У каждого пакета своя дата
              и свой порог по количеству проданных: срабатывает тот триггер, что раньше
            </Note>
          </Panel>

          <Panel title="Положение">
            <RegulationBox event={event} />
            <Note>
              Положение не генерируется: правила и регламент пишутся вручную. Система хранит файл
              и ссылку на него для кабинета руководителя
            </Note>
          </Panel>
        </Stack>

        <Stack>
          <PosterPanel event={event} />

          <Panel title="Готовность к публикации">
            <ConditionList>
              {event.readiness.map((c) => (
                <ConditionItem key={c.key} done={c.ok} name={c.name} hint={c.hint} />
              ))}
            </ConditionList>
            <Note>Пока мероприятие в черновике, продажи по нему не работают</Note>
          </Panel>
        </Stack>
      </Columns>
    </>
  );
}

/**
 * Афиша 3:2. Файл уходит на сервер сразу — отдельного «Сохранить» нет.
 * Загрузка — действием в шапке панели: действие живёт в блоке, которым
 * управляет (кит 2.11).
 */
function PosterPanel({ event }: { event: EventCard }) {
  const upload = useUploadPoster(event.id);
  const toast = useToast();
  const input = useRef<HTMLInputElement>(null);

  const pick = (file: File | undefined) => {
    if (!file) return;
    upload.mutate(file, {
      onSuccess: () => toast.show('Афиша загружена'),
      // Текст отказа — с сервера: там и лимит размера, и допустимые форматы
      onError: (e) => toast.showError(e instanceof ApiError ? e.message : 'Не загрузилось'),
    });
  };

  return (
    <Panel
      title="Афиша"
      actions={
        <Button small tone="quiet" onClick={() => input.current?.click()} disabled={upload.isPending}>
          {upload.isPending ? 'Загружаем…' : event.posterUrl ? 'Заменить' : 'Загрузить'}
        </Button>
      }
    >
      <input
        ref={input}
        type="file"
        accept="image/jpeg,image/png"
        style={{ display: 'none' }}
        onChange={(e) => pick(e.target.files?.[0])}
      />
      <div
        className="poster"
        style={event.posterUrl ? { backgroundImage: `url(${event.posterUrl})` } : undefined}
      >
        {event.posterUrl ? '' : 'афиша не загружена'}
      </div>
    </Panel>
  );
}

function RegulationBox({ event }: { event: EventCard }) {
  const upload = useUploadRegulation(event.id);
  const toast = useToast();
  const input = useRef<HTMLInputElement>(null);
  const [, force] = useState(0);

  const pick = (file: File | undefined) => {
    if (!file) return;
    upload.mutate(file, {
      onSuccess: () => {
        toast.show('Положение загружено');
        force((n) => n + 1);
      },
      onError: (e) => toast.showError(e instanceof ApiError ? e.message : 'Не загрузилось'),
    });
  };

  const hidden = (
    <input
      ref={input}
      type="file"
      accept="application/pdf"
      style={{ display: 'none' }}
      onChange={(e) => pick(e.target.files?.[0])}
    />
  );

  if (!event.regulationUrl) {
    return (
      <>
        {hidden}
        <EmptyState>
          Положение не загружено.
          <br />
          <TextLink onClick={() => input.current?.click()}>Загрузить PDF</TextLink>
        </EmptyState>
      </>
    );
  }

  return (
    <>
      {hidden}
      <Kv k="Файл">
        <a className="lnk" href={event.regulationUrl} target="_blank" rel="noreferrer">
          {event.regulationUrl.split('/').pop()}
        </a>
      </Kv>
      <Kv k="Замена">
        <TextLink onClick={() => input.current?.click()}>Загрузить другой PDF</TextLink>
      </Kv>
    </>
  );
}
